import { useState } from "react";
import { toast } from "sonner";
import { compressImage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Crosshair, Camera, X, MapPin } from "lucide-react";

function haversineM(lat1, lng1, lat2, lng2) {
  const r = 6371000;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dp = ((lat2 - lat1) * Math.PI) / 180;
  const dl = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(a));
}

export default function DoubleCheckDialog({ open, onOpenChange, occGeo, toleranceM = 100, onConfirm }) {
  const [photos, setPhotos] = useState([]);
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const hasRef = occGeo?.lat != null && occGeo?.lng != null;
  const distance = gps && hasRef ? haversineM(gps.lat, gps.lng, occGeo.lat, occGeo.lng) : null;
  const outside = distance != null && distance > toleranceM;

  const captureGps = () => {
    if (!navigator.geolocation) return toast.error("Geolocalização não suportada neste dispositivo");
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGpsLoading(false);
      },
      () => {
        setGpsLoading(false);
        toast.error("Não foi possível obter sua localização — verifique as permissões do navegador");
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const addPhotos = async (files) => {
    const arr = [];
    for (const f of Array.from(files || [])) arr.push(await compressImage(f));
    setPhotos((p) => [...p, ...arr].slice(0, 5));
  };

  const confirm = async () => {
    if (!photos.length) return toast.error("Registre pelo menos 1 foto no local");
    if (!gps) return toast.error("Capture sua localização GPS");
    setSubmitting(true);
    try {
      await onConfirm({ photos, gps });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="double-check-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldCheck size={18} className="text-cyan-400" /> Double Check em Campo
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Esta ocorrência exige validação presencial: registre uma foto no local e capture o GPS neste momento para confirmar a aprovação.
          </p>

          <div className="rounded-md border border-border p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium flex items-center gap-2"><Crosshair size={14} className="text-primary" /> Localização GPS</p>
              <Button variant="outline" size="sm" onClick={captureGps} disabled={gpsLoading} data-testid="dc-gps-btn">
                {gpsLoading ? "Capturando..." : gps ? "Recapturar" : "Capturar GPS"}
              </Button>
            </div>
            {gps ? (
              <div className="text-xs space-y-1">
                <p className="text-emerald-400">Posição: {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)} (±{Math.round(gps.accuracy || 0)} m)</p>
                {hasRef ? (
                  <p className={outside ? "text-red-400 font-semibold" : "text-emerald-400"} data-testid="dc-distance-info">
                    <MapPin size={11} className="inline mr-1" />
                    {Math.round(distance)} m do local da ocorrência (tolerância {toleranceM} m)
                    {outside && " — aproxime-se do local para aprovar"}
                  </p>
                ) : (
                  <p className="text-muted-foreground">Ocorrência sem GPS de referência — sua posição será registrada para auditoria.</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Nenhuma posição capturada ainda.</p>
            )}
          </div>

          <div className="rounded-md border border-border p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium flex items-center gap-2"><Camera size={14} className="text-primary" /> Foto no local *</p>
              <label htmlFor="dc-photos" className="text-xs text-primary cursor-pointer hover:underline">
                Adicionar foto
                <input id="dc-photos" type="file" accept="image/*" multiple className="hidden" data-testid="dc-photo-input"
                  onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
              </label>
            </div>
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative group">
                    <img src={p} alt={`double check ${i + 1}`} className="h-14 w-14 rounded-md object-cover border border-border" />
                    <button onClick={() => setPhotos((ps) => ps.filter((_, j) => j !== i))}
                      className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={confirm}
            disabled={submitting || !photos.length || !gps} data-testid="dc-confirm-btn">
            <ShieldCheck size={15} className="mr-2" /> {submitting ? "Validando..." : "Validar presença e aprovar conclusão"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
